import * as Collapsible from '@radix-ui/react-collapsible';
import { LinkComponent, createLink } from '@tanstack/react-router';
import { AnchorHTMLAttributes, ReactNode, forwardRef, useState } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

import {
  SolidBriefcaseBlank,
  SolidBuilding,
  SolidLocationDot,
} from '@/icons/components';

import { TextButton } from '../TextButton/TextButton';

interface BedrijfCardProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  title: string;
  location: string;
  tenant?: string | null;
  number?: string | null;
}

const BedrijfCard = forwardRef<HTMLAnchorElement, BedrijfCardProps>(
  ({ title, location, number, tenant, className, ...props }, ref) => {
    return (
      <a
        className={twMerge(
          twJoin(
            'group flex justify-between gap-2 rounded-sm border border-borderline-light bg-white px-3 py-2',
            'style-body text-gray-700',
            'hover:bg-gray-100 focus-visible:border-blue-500 focus-visible:ring-2 focus-visible:ring-blue-500 active:bg-gray-200'
          ),
          className
        )}
        ref={ref}
        {...props}
      >
        <div>
          <div className="style-h4-bold mb-0.5 text-black group-hover:text-blue-500">
            {title}
          </div>
          <div className="flex shrink items-center gap-1">
            <SolidBuilding />
            {location}
          </div>
        </div>

        <div className="relative min-h-full shrink-0 grow-0 pl-2 text-right">
          <div className="absolute left-0 top-0 h-full w-px bg-borderline-light" />

          {tenant && (
            <div className="mb-1 flex w-full shrink-0 items-center justify-end gap-1">
              <SolidBriefcaseBlank size={16} />
              {tenant}
            </div>
          )}

          {number && (
            <div className="flex shrink-0 items-center justify-end gap-1 text-right">
              <SolidLocationDot size={17} />
              {number}
            </div>
          )}
        </div>
      </a>
    );
  }
);

const CreatedBedrijfCardLink = createLink(BedrijfCard);

export const BedrijfCardLink: LinkComponent<typeof BedrijfCard> = (props) => {
  return <CreatedBedrijfCardLink {...props} />;
};

export const BedrijfCardCollapsibleWrapper = ({
  preview,
  hidden,
  moreButtonLabel = 'Meer tonen',
  lessButtonLabel = 'Minder tonen',
  children,
}: {
  preview: ReactNode;
  hidden: ReactNode;
  moreButtonLabel?: string;
  lessButtonLabel?: string;
  children: (className: string) => ReactNode;
}) => {
  const [open, setOpen] = useState(false);

  if (!preview && !hidden) {
    return children('');
  }

  return (
    <Collapsible.Root open={open} onOpenChange={setOpen}>
      {children('rounded-b-none')}

      <div className="-mt-px rounded-sm rounded-t-none border border-borderline-light bg-white px-3 py-2">
        {preview}

        {hidden && (
          <>
            <Collapsible.Content>{hidden}</Collapsible.Content>

            <Collapsible.Trigger asChild>
              <TextButton className="mt-1">
                {open ? lessButtonLabel : moreButtonLabel}
              </TextButton>
            </Collapsible.Trigger>
          </>
        )}
      </div>
    </Collapsible.Root>
  );
};
